// اجزای مشترک لاگ پیام (SCR-STAFF-954) و تاریخچه پرونده (COMP-PATIENT-MESSAGES): ستون‌ها و جزئیات پیام با متن فقط برای
// msg.log.view_content (US-MSG-071).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type MessageLogItem } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ErrorState,
  faDigits,
  JalaliDate,
  KeyValueList,
  LoadingState,
  type DataTableColumn,
} from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';
import { Link as RouterLink } from 'react-router';
import { MessageStatusChip, msgKeys, usePurposeLabel, useReasonLabel, useTemplateLabel } from './common';

/** ستون‌های مشترک لاگ و تاریخچه مراجعه‌کننده. */
export function useMessageColumns(
  onOpen: (row: MessageLogItem) => void,
  withPatient: boolean,
): DataTableColumn<MessageLogItem>[] {
  const { t } = useTranslation('msg');
  const purposeLabel = usePurposeLabel();
  const templateLabel = useTemplateLabel();
  const reasonLabel = useReasonLabel();
  return [
    {
      id: 'time',
      headerKey: 'msg:log.columns.time',
      cell: (row) => <JalaliDate value={row.createdAt} withTime />,
    },
    ...(withPatient
      ? [
          {
            id: 'patient',
            headerKey: 'msg:log.columns.recipient',
            cell: (row: MessageLogItem) =>
              row.patientId ? (
                <Link component={RouterLink} to={`/patients/${row.patientId}?tab=messages`}>
                  {t('msg:log.patient')}
                </Link>
              ) : (
                <bdi dir="ltr">{row.recipientLast4 ? `…${faDigits(row.recipientLast4)}` : ''}</bdi>
              ),
          },
        ]
      : []),
    { id: 'purpose', headerKey: 'msg:log.columns.purpose', cell: (row) => purposeLabel(row.purpose) },
    { id: 'template', headerKey: 'msg:log.columns.template', cell: (row) => templateLabel(row.templateCode) },
    {
      id: 'channel',
      headerKey: 'msg:log.columns.channel',
      cell: (row) => t(`msg:channel.${row.channel}`, { defaultValue: row.channel }),
    },
    {
      id: 'status',
      headerKey: 'msg:log.columns.status',
      cell: (row) => (
        <Stack spacing={0.25}>
          <MessageStatusChip status={row.status} testId={`message-status-${row.id}`} />
          {row.reason ? (
            <span data-testid={`message-reason-${row.id}`}>{reasonLabel(row.reason)}</span>
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'sender',
      headerKey: 'msg:log.columns.sender',
      cell: (row) => row.senderName ?? t('msg:log.system'),
    },
    {
      id: 'actions',
      headerKey: 'msg:log.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => onOpen(row)} data-testid={`message-open-${row.id}`}>
          {t('msg:log.details')}
        </Button>
      ),
    },
  ];
}

/** جزئیات یک پیام با متن (در صورت مجوز). */
export function MessageDetailDialog({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const titleId = useId();
  const purposeLabel = usePurposeLabel();
  const templateLabel = useTemplateLabel();
  const reasonLabel = useReasonLabel();
  const detail = useQuery({
    queryKey: msgKeys.message(session?.tenantId, messageId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/messaging/messages/{id}', { params: { path: { id: messageId } } })),
  });
  const message = detail.data?.message;
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{t('msg:log.detail_title')}</DialogTitle>
      <DialogContent>
        {detail.isPending ? <LoadingState rows={3} /> : null}
        {detail.error ? <ErrorState error={detail.error} compact /> : null}
        {message ? (
          <Stack spacing={2}>
            <KeyValueList
              columns={2}
              items={[
                {
                  labelKey: 'msg:log.columns.time',
                  value: <JalaliDate value={message.createdAt} withTime />,
                },
                { labelKey: 'msg:log.columns.status', value: <MessageStatusChip status={message.status} /> },
                { labelKey: 'msg:log.columns.purpose', value: purposeLabel(message.purpose) },
                { labelKey: 'msg:log.columns.template', value: templateLabel(message.templateCode) },
                { labelKey: 'msg:log.reason', value: reasonLabel(message.reason) || null },
                { labelKey: 'msg:log.parts', value: faDigits(String(message.parts)) },
                {
                  labelKey: 'msg:log.sent_at',
                  value: message.sentAt ? <JalaliDate value={message.sentAt} withTime /> : null,
                },
                {
                  labelKey: 'msg:log.delivered_at',
                  value: message.deliveredAt ? <JalaliDate value={message.deliveredAt} withTime /> : null,
                },
              ]}
            />
            {detail.data?.contentHidden ? (
              <Alert severity="info" data-testid="message-content-hidden">
                {t('msg:log.content_hidden')}
              </Alert>
            ) : detail.data?.content ? (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography sx={{ whiteSpace: 'pre-wrap' }} data-testid="message-content">
                  {detail.data.content}
                </Typography>
              </Paper>
            ) : (
              <Alert severity="info" data-testid="message-content-purged">
                {t('msg:log.content_purged')}
              </Alert>
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
